import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { dealerApi, API_BASE_URL, DEALER_STORAGE_KEY } from "../lib/api";
import { shortDate, tzs } from "../lib/format";
import { PageHead } from "../components/PageHead";
import { useDealerSession } from "../lib/session";

interface EntryRow {
  id: string;
  order_ref: string;
  mno_code: string;
  amount_minor: number;
  agent_commission_minor: number;
  dealer_commission_minor: number;
  status: string;
  reason: string | null;
  created_at: string | null;
}

const PAGE_SIZE = 25;

export function Commissions() {
  const { dealer } = useDealerSession();
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  const entries = useQuery({
    queryKey: ["dealer-commissions", page],
    queryFn: () =>
      dealerApi.get<{ data: EntryRow[]; total: number }>(
        `/superdealer/v1/commissions?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
      ),
  });

  const dash = useQuery({
    queryKey: ["dealer-dashboard"],
    queryFn: () =>
      dealerApi.get<{
        commission_month_minor: number;
        commission_total_minor: number;
      }>("/superdealer/v1/dashboard"),
  });

  const total = entries.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportCsv = async () => {
    setExporting(true);
    try {
      let token: string | null = null;
      try {
        const raw = localStorage.getItem(DEALER_STORAGE_KEY);
        token = raw ? (JSON.parse(raw)?.access_token as string) : null;
      } catch {
        /* ignore */
      }
      const res = await fetch(`${API_BASE_URL}/superdealer/v1/commissions/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commissions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* the button is best-effort; the table remains the source of truth */
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHead
        title="Commissions"
        description="Your dealer share from every settled sale in your network."
        actions={
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={() => void exportCsv()} disabled={exporting}>
            <Download size={15} style={{ marginRight: 6 }} />
            {exporting ? "Preparing…" : "Export CSV"}
          </button>
        }
      />

      <div className="dash-kpi-row" style={{ marginBottom: 16 }}>
        <div className="dash-kpi dash-kpi-green">
          <span className="dash-kpi-label">This month</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(dash.data?.commission_month_minor)}</span>
          </div>
        </div>
        <div className="dash-kpi dash-kpi-navy">
          <span className="dash-kpi-label">Earned to date</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(dash.data?.commission_total_minor)}</span>
          </div>
        </div>
        <div className="dash-kpi dash-kpi-violet">
          <span className="dash-kpi-label">Commission wallet</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{dealer?.code ?? "—"}</span>
          </div>
          <span className="dash-kpi-note">Sweep to available from Settings</span>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Commission ledger</h3>
          <span className="ev-body-micro muted">{total} entries</span>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-table-th">Order</th>
                <th className="admin-table-th">Network</th>
                <th className="admin-table-th">Sale amount</th>
                <th className="admin-table-th">Agent share</th>
                <th className="admin-table-th">My share</th>
                <th className="admin-table-th">Status</th>
                <th className="admin-table-th">Date</th>
              </tr>
            </thead>
            <tbody>
              {(entries.data?.data ?? []).map((e) => (
                <tr key={e.id} className="admin-table-row-hover">
                  <td className="admin-table-td dash-mono">{e.order_ref}</td>
                  <td className="admin-table-td">{e.mno_code}</td>
                  <td className="admin-table-td">{tzs(e.amount_minor)}</td>
                  <td className="admin-table-td">{tzs(e.agent_commission_minor)}</td>
                  <td className="admin-table-td">
                    <strong>{tzs(e.dealer_commission_minor)}</strong>
                  </td>
                  <td className="admin-table-td">
                    <span className={`dash-pill ${e.status === "earned" ? "dash-pill-success" : "dash-pill-review"}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="admin-table-td ev-body-micro muted">{shortDate(e.created_at)}</td>
                </tr>
              ))}
              {!entries.isLoading && !entries.data?.data.length && (
                <tr>
                  <td className="admin-table-td" colSpan={7}>
                    <div className="dash-table-empty">
                      No commission yet. Entries appear when your agents complete sales.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination">
          <span className="ev-body-micro muted">
            Page {page + 1} of {pages}
          </span>
          <div className="dealer-filter-row">
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
